import { useEffect, useState } from "react";
import {
  Airplane,
  ArrowToDownHeader,
  CheckMarkIcon,
  CopyIcon,
  HandKey,
  LightIcon,
  Logo,
  NotificationBingIconHeader,
  SwapIconHeader,
  UnVerified,
  UserBoxIcon,
  VerifyUserIcon,
} from "../AssetComponents/Images";
import { PurpleButton } from "./Buttons/PurpleButton";
import { Popover } from "react-tiny-popover";
import { SelectionGroup, SelectionItem } from "./Selection/Selection";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setConnectModal } from "../ReduxSlices/ModalSlice";
import {
  truncateWalletAddress,
  getProfileFromWallet,
  navigateToMessageNotificationInbox,
  resolveNftFromStore,
  isSameWalletAddress,
} from "./functions/Functions";
import { BlackButton } from "./Buttons/BlackButton";
import CopyToClipboard from "react-copy-to-clipboard";
import { setDisconnect } from "../ReduxSlices/AuthSlice";
import { removeNotificationById, setLastViewedAt } from "../ReduxSlices/NotificationSlice";
import { Testnet, Mainnet } from "@nibiruchain/nibijs";
import { setEthereum } from "../ReduxSlices/AuthSlice";
import { useDisconnect, useActiveWallet } from "thirdweb/react";
import { api } from "./functions/Api";
import keplrIcon from "../assets/images/Keplr_icon_ver.1.3_2.svg";
import { ChartCandlestick, Landmark, Menu, X } from 'lucide-react';
import { RedButton } from "./Buttons/RedButton";
import { NotificationImageWithFallback } from "./Images/ImageWithFallback";

/**
 * RENTAL PRODUCT HEADER - PERPS NAVIGATION REMOVED
 * 
 * This header component has been configured for rental functionality only:
 * - Removed all perps navigation (Markets, Vaults)
 * - Only shows "Rent" and "Dashboard" navigation
 * - Dashboard includes Host and Traveler modes
 * - MetaMask support removed - only Leap and Keplr wallets supported
 * - Mobile navigation also updated for rental-only functionality
 */

// MetaMask icon removed - only Leap and Keplr supported for rental product

const getWalletIcon = (walletType) => {
  switch (walletType) {
    case "leap":
      return "https://assets.leapwallet.io/logos/leap-cosmos-logo.svg";
    case "keplr":
      return keplrIcon;
    case "metamask":
      return "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg";
    case "thirdweb":
      return "https://thirdweb.com/favicon.ico";
    default:
      return "";
  }
};

const safeFormatDate = (timeUTC, diff, options) => {
  if (!timeUTC || isNaN(new Date(timeUTC).getTime())) {
    return "N/A"; // Fallback
  }
  return new Intl.DateTimeFormat("en-US", options).format(
    (Math.round(new Date(timeUTC).getTime() / 1000) - diff) * 1000
  );
};

export const Header = () => {
  const [showDashboardMenu, setShowDashboardMenu] = useState(false);
  const [showTradeMenu, setShowTradeMenu] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const isConnected = useSelector((state) => state.auth.isConnected);
  const account = useSelector((state) => state.auth.account);
  const wallet = useSelector((state) => state.auth.wallet);
  const diff = useSelector((state) => state.time.diffToUTC);
  // Ethereum/MetaMask state removed - only Leap and Keplr supported for rental product
  const profile = useSelector((state) => state.auth.profile);
  const nfts = useSelector((state) => state.nft.nfts);
  const notificationContent = useSelector((state) => state.notification.items);
  const lastViewedAt = useSelector((state) => state.notification.lastViewedAt);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const [current, setCurrent] = useState();
  const [maskString, setMaskString] = useState("shadow-md rounded-[10px]");
  const mainnet = Mainnet();
  const [profiles, setProfiles] = useState({});
  const { disconnect: disconnectThirdweb } = useDisconnect();
  const activeThirdwebWallet = useActiveWallet();

  // This is a rental product - no perps navigation needed
  const isRentalPage = location.pathname.startsWith('/rent') || location.pathname.startsWith('/dashboard');

  const tryConnect = () => {
    dispatch(setConnectModal(true));
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const initiateCopy = async () => {
    await delay(2000);
    setCopied(false);
  };

  const handleDisconnect = async () => {
    try {
      if (wallet === "thirdweb" && activeThirdwebWallet) {
        await disconnectThirdweb(activeThirdwebWallet);
      } else if (wallet === "leap" && window.leap) {
        await window.leap.disconnect(mainnet.chainId);
      } else if (wallet === "keplr" && window.keplr) {
        await window.keplr.disable(mainnet.chainId);
      }
    } catch (err) {
      console.error("Error disconnecting wallet:", err);
    } finally {
      dispatch(setDisconnect());
    }
  };

  const handleMobileNavigation = (path, currentValue) => {
    navigate(path);
    setCurrent(currentValue);
    setShowMobileMenu(false);
  };

  useEffect(() => {
    if (isConnected) setShowConnect(false);
  }, [isConnected]);

  useEffect(() => {
    if (copied) initiateCopy();
  }, [copied]);

  useEffect(() => {
    setMaskString("shadow-md rounded-[10px]");
    const pathParts = location.pathname.split("/");

    // Rental product navigation: Rent(0), Dashboard(1)
    switch (pathParts[1]) {
      case "rent":
        setCurrent(0);
        break;
      case "dashboard":
        setCurrent(1);
        break;
      default:
        setCurrent(-1);
        setMaskString("");
        break;
    }

    // Close any open menus when navigating
    setShowDashboardMenu(false);
    setShowNotification(false);
  }, [location.pathname]);

  const getProfile = async (address, profiles) => {
    if (profiles[address] || profiles[address] === false) return;
    const profile = await getProfileFromWallet(address);
    profiles[address] = profile;
  };

  const getProfiles = async () => {
    let tempProfiles = {};
    for (let i = 0; i < notificationContent.length; i++) {
      if (notificationContent[i].type === "message") {
        getProfile(notificationContent[i].data.message.sender, tempProfiles);
      } else {
        getProfile(notificationContent[i].data.sender, tempProfiles);
      }
    }
    setProfiles(tempProfiles);
  };

  useEffect(() => {
    getProfiles();
  }, [notificationContent]);

  // Thirdweb/MetaMask integration removed - only Leap and Keplr supported for rental product

  // Add debugging for notification state
  useEffect(() => {
    console.log(" Notification state changed:", {
      showNotification,
      notificationCount: notificationContent.length,
      notifications: notificationContent
    });
  }, [showNotification, notificationContent]);

  // Helper function to check if there are unviewed notifications
  const hasUnviewedNotifications = () => {
    if (!lastViewedAt) {
      // If never viewed, show red dot if there are any notifications
      return notificationContent.length > 0;
    }

    // Check if any notification arrived after lastViewedAt
    return notificationContent.some((item) => {
      let notificationTime = null;

      if (item.type === "event" && item.data.timeUTC) {
        notificationTime = new Date(item.data.timeUTC).getTime();
      } else if (item.type === "message" && item.data.message?.generatedTime) {
        notificationTime = new Date(item.data.message.generatedTime).getTime();
      }

      // If we can't determine the time, treat it as unviewed
      if (!notificationTime) return true;

      // Notification is unviewed if it arrived after lastViewedAt
      return notificationTime > lastViewedAt;
    });
  };

  const parseAction = (action) => {
    switch (action) {
      case "finalize_short_term_rental":
        return (
          <div className="flex items-center">
            <div className="text-[#38A569]">Completed</div>
          </div>
        );
      case "set_reservation_for_short_term":
        return (
          <div className="flex items-center">
            <div className="text-[#38A569]">Reserved</div>
          </div>
        );
      case "cancel_reservation_for_shortterm":
        return (
          <div className="flex items-center">
            <div className="text-[#DB1F22]">Canceled</div>
          </div>
        );
      case "cancel_rental_for_shortterm":
        return (
          <div className="flex items-center">
            <div className="text-[#DB1F22]">Canceled</div>
          </div>
        );
      case "reject_reservation_for_shortterm":
        return (
          <div className="flex items-center">
            <div className="text-[#DB1F22]">Rejected</div>
          </div>
        );
      default:
        break;
    }
  };

  const normalizeAddress = (value) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";

  const getNotificationPath = (item, account, nfts) => {
    if (item.type === "message") {
      const nft = resolveNftFromStore(nfts, item.data?.nftId);
      const isPropertyOwner =
        nft?.access?.owner &&
        isSameWalletAddress(account, nft.access.owner);
      return isPropertyOwner
        ? `/dashboard/host/inbox?chat=${item.data.id}`
        : `/dashboard/traveler/inbox?chat=${item.data.id}`;
    }

    if (item.type === "event") {
      // Ensure token_id is valid
      const tokenId = item.data.token_id;
      if (!tokenId || tokenId === 'undefined' || tokenId === 'null') {
        return "/dashboard";
      }

      const nft = nfts[tokenId];

      const accountNorm = normalizeAddress(account);
      const ownerNorm = normalizeAddress(
        (nft && (nft.access?.owner || nft.createdByWallet)) || ""
      );
      const senderNorm = normalizeAddress(item.data.sender);
      const receiverNorm = normalizeAddress(item.data.receiver);

      let isOwnerView = false;

      // Primary check: direct owner match (covers most EVM-host cases)
      if (accountNorm && ownerNorm && accountNorm === ownerNorm) {
        isOwnerView = true;
      } else if (accountNorm) {
        const reservationActions = new Set([
          "set_reservation_for_short_term",
          "cancel_reservation_for_shortterm",
          "cancel_rental_for_shortterm",
          "reject_reservation_for_shortterm",
          "finalize_short_term_rental",
        ]);

        if (reservationActions.has(item.data.action)) {
          // For reservation-related events:
          // - sender is always the traveler
          // - receiver is always the host
          if (accountNorm === receiverNorm) {
            isOwnerView = true; // Host viewing the notification
          } else if (accountNorm === senderNorm) {
            isOwnerView = false; // Traveler viewing the notification
          }
        } else {
          // For other events (e.g. mint/property updates), treat sender as owner/host
          if (accountNorm === senderNorm) {
            isOwnerView = true;
          }
        }
      }

      // Helper function to find renting_period from NFT data
      const getRentingPeriod = () => {
        // First, try to get from notification data directly
        if (item.data.renting_period && Array.isArray(item.data.renting_period) && item.data.renting_period.length === 2) {
          return item.data.renting_period;
        }

        // Otherwise, try to find from NFT rentals array
        if (nft?.rentals?.rentals && Array.isArray(nft.rentals.rentals)) {
          const action = item.data.action;
          const sender = item.data.sender;

          // For cancel/reject actions, find the cancelled/rejected reservation
          if (action === "cancel_reservation_for_shortterm" ||
            action === "cancel_rental_for_shortterm" ||
            action === "reject_reservation_for_shortterm") {
            // Find the most recent cancelled/rejected reservation
            for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
              const reservation = nft.rentals.rentals[i];
              if (reservation.renting_period &&
                Array.isArray(reservation.renting_period) &&
                reservation.renting_period.length === 2 &&
                (reservation.cancelled || (!reservation.approved && action === "reject_reservation_for_shortterm"))) {
                return reservation.renting_period;
              }
            }
          }

          // For finalize action, find the most recent finalized (approved) reservation
          if (action === "finalize_short_term_rental") {
            for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
              const reservation = nft.rentals.rentals[i];
              if (reservation.renting_period &&
                Array.isArray(reservation.renting_period) &&
                reservation.renting_period.length === 2 &&
                reservation.approved &&
                !reservation.cancelled) {
                return reservation.renting_period;
              }
            }
          }

          // For set_reservation, find the most recent reservation that matches the sender
          if (action === "set_reservation_for_short_term") {
            for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
              const reservation = nft.rentals.rentals[i];
              if (reservation.renting_period &&
                Array.isArray(reservation.renting_period) &&
                reservation.renting_period.length === 2 &&
                reservation.address === sender) {
                return reservation.renting_period;
              }
            }
          }

          // Fallback: get the most recent reservation with valid renting_period
          for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
            const reservation = nft.rentals.rentals[i];
            if (reservation.renting_period &&
              Array.isArray(reservation.renting_period) &&
              reservation.renting_period.length === 2) {
              return reservation.renting_period;
            }
          }
        }

        return null;
      };

      switch (item.data.action) {
        case "set_reservation_for_short_term":
        case "finalize_short_term_rental":
        case "cancel_reservation_for_shortterm":
        case "cancel_rental_for_shortterm":
        case "reject_reservation_for_shortterm":
          const rentingPeriod = getRentingPeriod();
          if (rentingPeriod && rentingPeriod.length === 2) {
            const from = rentingPeriod[0];
            const to = rentingPeriod[1];
            return isOwnerView
              ? `/dashboard/host/reservations/${tokenId}?from=${from}&to=${to}`
              : `/dashboard/traveler/trips/${tokenId}?from=${from}&to=${to}`;
          }

          // Fallback to reservations with token_id if we can't find renting_period
          return isOwnerView
            ? `/dashboard/host/reservations/${tokenId}`
            : `/dashboard/traveler/trips/${tokenId}`;

        default:
          // For any other event with a token_id, redirect to reservations
          return isOwnerView
            ? `/dashboard/host/reservations/${tokenId}`
            : `/dashboard/traveler/trips/${tokenId}`;
      }
    }

    return "/dashboard";
  };

  // Don't render header on onboarding pages
  if (location.pathname === '/rent/onboarding' || location.pathname === '/rent/onboarding/Completion' || location.pathname === '/rent/onboarding/privacy' || location.pathname === '/rent/onboarding/terms' || location.pathname === '/rent/onboarding/about' || location.pathname === '/onboarding/steps') {
    return null;
  }

  // Render navigation items for rental product
  const renderNavigationItems = () => {
    const items = [];

    // Rent navigation
    items.push(
      <SelectionItem
        key="rent"
        SelectedItem={
          <div className="cursor-pointer bg-white rounded-[10px] w-[120px] flex justify-center gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
            <div className="text-black font-semibold">Rent</div>
            <LightIcon />
          </div>
        }
        UnselectedItem={
          <div
            className="cursor-pointer hover:bg-white rounded-[10px] w-[120px] flex justify-center"
            onClick={() => {
              navigate("/rent/all");
              setCurrent(0);
              setShowDashboardMenu(false);
            }}
          >
            <div className="text-[#959595]">Rent</div>
          </div>
        }
      />
    );

    // Dashboard navigation
    items.push(
      <SelectionItem
        key="dashboard"
        SelectedItem={
          <Popover
            isOpen={showDashboardMenu}
            positions={"bottom"}
            onClickOutside={() => {}}
            containerStyle={{ zIndex: 99999999 }}
            content={
              <div
                onClick={() => {
                  setShowDashboardMenu(false);
                }}
                className="mt-[10px] bg-white rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
              >
                <div
                  onClick={() => {
                    navigate("/dashboard/host/dashboard");
                    setCurrent(1);
                  }}
                  className="cursor-pointer hover:bg-[#f5f5f5] rounded-t-[10px] p-[8px] gap-[8px] flex items-center w-full"
                >
                  <HandKey />
                  <div className="text-[#666666]">Host mode</div>
                </div>
                <div
                  onClick={() => {
                    navigate("/dashboard/traveler/dashboard");
                    setCurrent(1);
                  }}
                  className="cursor-pointer hover:bg-[#f5f5f5] rounded-b-[10px] p-[8px] gap-[8px] flex items-center w-max"
                >
                  <Airplane />
                  <div className="text-[#666666]">Traveler mode</div>
                </div>
              </div>
            }
          >
            <div
              onClick={() => setShowDashboardMenu(true)}
              className="cursor-pointer bg-white rounded-[10px] w-[120px] flex justify-center gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
            >
              <div className="text-black font-semibold">Dashboard</div>
              <LightIcon />
            </div>
          </Popover>
        }
        UnselectedItem={
          <Popover
            isOpen={showDashboardMenu}
            positions={"bottom"}
            onClickOutside={() => {}}
            containerStyle={{ zIndex: 99999999 }}
            content={
              <div
                onClick={() => {
                  setShowDashboardMenu(false);
                }}
                className="mt-[10px] bg-white rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
              >
                <div
                  onClick={() => {
                    navigate("/dashboard/host/dashboard");
                    setCurrent(1);
                  }}
                  className="cursor-pointer hover:bg-[#f5f5f5] rounded-t-[10px] p-[8px] gap-[8px] flex items-center w-full"
                >
                  <HandKey />
                  <div className="text-[#666666]">Host mode</div>
                </div>
                <div
                  onClick={() => {
                    navigate("/dashboard/traveler/dashboard");
                    setCurrent(1);
                  }}
                  className="cursor-pointer hover:bg-[#f5f5f5] rounded-b-[10px] p-[8px] gap-[8px] flex items-center w-max"
                >
                  <Airplane />
                  <div className="text-[#666666]">Traveler mode</div>
                </div>
              </div>
            }
          >
            <div
              className="cursor-pointer hover:bg-white rounded-[10px] w-[120px] flex justify-center gap-[4px]"
              onClick={() => setShowDashboardMenu(true)}
            >
              <div className="text-[#959595]">Dashboard</div>
              <ArrowToDownHeader />
            </div>
          </Popover>
        }
      />
    );

    return items;
  };

  return (
    <>
      <div className="relative">
        {current !== 4 ? (
          <>
            <div className="bg-white flex w-full items-center justify-between py-[14px] px-[16px] md:grid md:grid-cols-3 md:justify-items-center relative z-[100]">
             
              <Logo
                onClick={() => {
                  navigate("/");
                }}
                className="justify-self-start cursor-pointer"
              />

              <div className="hidden md:block">
                <SelectionGroup
                  key={`nav-${current}`}
                  className="px-[6px] py-[4px] gap-[8px] flex items-center rounded-[14px] bg-[#f6f6f6] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                  defaultItem={current >= 0 ? current : 0}
                >
                  {renderNavigationItems()}
                </SelectionGroup>
              </div>

              <div className="flex items-center justify-self-end">
                {/* Swap Icon - Desktop only */}
                <div
                  data-tooltip-id="my-tooltip"
                  className="hidden md:block mr-[20px] hover:bg-[#f6f6f6] hover:translate-y-[-4px] cursor-pointer bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-[10px] rounded-full"
                >
                  <SwapIconHeader />
                </div>

                <Popover
                  isOpen={showNotification}
                  positions={"bottom"}
                  onClickOutside={() => {
                    console.log(" Notification popover clicked outside");
                    setShowNotification(false);
                    // 🚀 GLOBAL NOTIFICATION VIEWED STATE: Mark notifications as viewed via API
                    if (account) {
                      api("user/markNotificationsAsViewed", { account })
                        .then((response) => {
                          if (response?.success && response.lastViewedAt !== undefined) {
                            dispatch(setLastViewedAt(response.lastViewedAt));
                          }
                        })
                        .catch((error) => {
                          console.warn("Failed to mark notifications as viewed:", error);
                        });
                    }
                  }}
                  containerStyle={{ zIndex: 99999999 }}
                  content={
                    <div className="p-[10px] bg-white mt-[10px] w-[300px] h-[500px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                      <div className="flex items-center gap-2 mb-2">
                        <svg
                          className="w-[20px] h-[20px]"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8 14V14C4.686 14 2 11.314 2 8V8C2 4.686 4.686 2 8 2V2C11.314 2 14 4.686 14 8V8C14 11.314 11.314 14 8 14Z"
                            stroke="#505050"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.9987 11.3333V8H7.33203"
                            stroke="#505050"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.83267 5.33333C7.74067 5.33333 7.666 5.408 7.66667 5.5C7.66667 5.592 7.74134 5.66667 7.83334 5.66667C7.92534 5.66667 8 5.592 8 5.5C8 5.408 7.92534 5.33333 7.83267 5.33333"
                            stroke="#505050"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="font-semibold text-[18px]">
                          Notifications
                        </div>
                      </div>

                      {notificationContent.length > 0 ? (
                        <div className="space-y-[4px] h-[90%] w-full overflow-auto scrollbarwidth">
                          {notificationContent
                            .slice()
                            .reverse()
                            .map((item, i) => {
                              if (item.type === "event")
                                return (
                                  <div
                                    key={i}
                                    onClick={() => {
                                      console.log(" Notification clicked:", item);
                                      setShowNotification(false);
                                      // Remove notification when clicked
                                      dispatch(removeNotificationById({
                                        type: "event",
                                        identifier: {
                                          token_id: item.data.token_id,
                                          sender: item.data.sender,
                                          action: item.data.action,
                                          txHash: item.data.txHash,
                                        }
                                      }));
                                      const path = getNotificationPath(item, account, nfts);
                                      navigate(path);
                                    }}
                                    className="hover:bg-[#f0f0f0] rounded-[4px] cursor-pointer p-[6px] w-full flex items-center justify-between"
                                  >
                                    <NotificationImageWithFallback
                                      src={profiles[item.data.sender]?.Avatar}
                                      alt="User Avatar"
                                      className="shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                                      size="lg"
                                    />

                                    <div>
                                      <div>
                                        {profiles[item.data.sender]?.Name
                                          ? profiles[
                                            item.data.sender
                                          ]?.Name?.replace("/", " ")
                                          : truncateWalletAddress(item.data.sender)}
                                      </div>
                                      <div className="flex gap-2 w-[150px] truncate">
                                        <span className="font-semibold">
                                          {parseAction(item.data.action)}
                                        </span>
                                        <span className="truncate">
                                          {
                                            nfts[item.data.token_id]?.metaData
                                              ?.buildingName || "Property"
                                          }
                                        </span>
                                      </div>
                                    </div>

                                    <div>
                                      <div className="text-[12px] text-end">
                                        {safeFormatDate(item.data.timeUTC, diff, {
                                          day: "numeric",
                                          month: "short",
                                        })}
                                      </div>
                                      <div className="text-[12px] text-end">
                                        {safeFormatDate(item.data.timeUTC, diff, {
                                          hour: "numeric",
                                          minute: "2-digit",
                                          hour12: true,
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                );
                              else
                                return (
                                  <div
                                    key={i}
                                    onClick={async () => {
                                      console.log(" Message notification clicked:", item);
                                      setShowNotification(false);
                                      // Remove notification when clicked
                                      dispatch(removeNotificationById({
                                        type: "message",
                                        identifier: {
                                          chatId: item.data.id,
                                        }
                                      }));
                                      await navigateToMessageNotificationInbox({
                                        item,
                                        account,
                                        nfts,
                                        pathname: location.pathname,
                                        navigate,
                                      });
                                    }}
                                    className="hover:bg-[#f0f0f0] rounded-[4px] cursor-pointer p-[6px] w-full flex items-center justify-between"
                                  >
                                    {profiles[item.data.message.sender]?.Avatar ? (
                                      <img
                                        alt=""
                                        src={
                                          profiles[item.data.message.sender]?.Avatar
                                        }
                                        className="w-[44px] h-[44px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                                      />
                                    ) : (
                                      <div className="w-[44px] h-[44px] flex bg-[#f0f0f0] rounded-full">
                                        <UserBoxIcon className="m-auto" />
                                      </div>
                                    )}

                                    <div>
                                      <div>
                                        {profiles[item.data.message.sender]?.Name
                                          ? profiles[
                                            item.data.message.sender
                                          ]?.Name?.replace("/", " ")
                                          : truncateWalletAddress(
                                            item.data.message.sender
                                          )}
                                      </div>

                                      <div className="flex gap-2 w-[150px] truncate">
                                        <span className="truncate">
                                          Sent new message
                                        </span>
                                      </div>
                                    </div>

                                    <div>
                                      <div className="text-[12px] text-end">
                                        {safeFormatDate(
                                          item.data.message.generatedTime,
                                          diff,
                                          {
                                            day: "numeric",
                                            month: "short",
                                          }
                                        )}
                                      </div>
                                      <div className="text-[12px] text-end">
                                        {safeFormatDate(
                                          item.data.message.generatedTime,
                                          diff,
                                          {
                                            hour: "numeric",
                                            minute: "2-digit",
                                            hour12: true,
                                          }
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                            })}
                        </div>
                      ) : (
                        <div className="w-full h-[80%] flex">
                          <div className="m-auto">No notifications yet</div>
                        </div>
                      )}
                    </div>
                  }
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log(" Notification icon clicked, current state:", showNotification);
                      const willOpen = !showNotification;
                      setShowNotification(willOpen);

                      // 🚀 GLOBAL NOTIFICATION VIEWED STATE: Mark notifications as viewed via API
                      if (willOpen && account) {
                        api("user/markNotificationsAsViewed", { account })
                          .then((response) => {
                            if (response?.success && response.lastViewedAt !== undefined) {
                              dispatch(setLastViewedAt(response.lastViewedAt));
                            }
                          })
                          .catch((error) => {
                            console.warn("Failed to mark notifications as viewed:", error);
                          });
                      }
                    }}
                    className="mr-[20px] md:mr-[40px] hover:bg-[#f6f6f6] hover:translate-y-[-4px] relative cursor-pointer bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-[10px] rounded-full"
                  >
                    {hasUnviewedNotifications() ? (
                      <div className="absolute top-0 right-[6px] bg-red-500 rounded-full w-[10px] h-[10px]"></div>
                    ) : (
                      <></>
                    )}
                    <NotificationBingIconHeader />
                  </div>
                </Popover>

                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMobileMenu(!showMobileMenu);
                  }}
                  className="md:hidden cursor-pointer bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-[10px] rounded-full hover:bg-[#f6f6f6]"
                >
                  {showMobileMenu ? <X size={20} /> : <Menu size={20} />}
                </div>

                <div className="hidden md:block">
                  <Popover
                    isOpen={showConnect && isConnected}
                    onClickOutside={() => setShowConnect(false)}
                    positions={"bottom"}
                    containerStyle={{ zIndex: 99999999 }}
                    content={
                      <div className="mr-[16px] flex flex-col items-center p-[20px] mt-[10px] bg-white rounded-[10px] w-[300px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              setShowConnect(false);
                              // Default to traveler profile view from rental header
                              navigate("/dashboard/profile?mode=traveler");
                            }}
                            className="cursor-pointer rounded-full p-0 border-0 bg-transparent hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#38A569] focus:ring-offset-1"
                            aria-label="Go to profile"
                          >
                            {profile?.Avatar ? (
                              <img
                                alt="Profile"
                                src={profile?.Avatar}
                                className="w-[100px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                              />
                            ) : (
                              <VerifyUserIcon className="w-[120px]" />
                            )}
                          </button>

                          <svg
                            className="w-[60px] h-[40px]"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.5 16L17.5 12L13.5 8"
                              stroke="#38A569"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.5 17L11.5 12L6.5 7"
                              stroke="#38A569"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <img
                            alt=""
                            src={getWalletIcon(wallet)}
                            className="w-[80px] h-[80px]"
                          />
                        </div>
                        <CopyToClipboard
                          text={account}
                          onCopy={() => setCopied(true)}
                        >
                          <div className="w-full mt-3">
                            <PurpleButton
                              text={
                                <div className="flex items-center gap-3 justify-center">
                                  <span>
                                    {truncateWalletAddress(account)?.toUpperCase()}
                                  </span>
                                  {copied && <CheckMarkIcon />}
                                </div>
                              }
                            />
                          </div>
                        </CopyToClipboard>
                        <div className="w-full mt-3">
                          <BlackButton text="Disconnect" onClick={handleDisconnect} />
                        </div>
                      </div>
                    }
                  >
                    <div
                      className="w-max h-max"
                      onClick={() => {
                        if (isConnected) setShowConnect(!showConnect);
                        else tryConnect();
                      }}
                    >
                      <PurpleButton
                        text={
                          isConnected
                            ? truncateWalletAddress(account)
                            : "Connect Wallet"
                        }
                      />
                    </div>
                  </Popover>
                </div>
              </div>
            </div>

            {/* MOBILE MENU OVERLAY */}
            {showMobileMenu && (
              <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg z-[101] border-t">
                <div className="p-4 space-y-4">
                  {/* Mobile Navigation Links */}

                  {/* Rent Submenu */}
                  <div className="border-l-2 border-[#f0f0f0] pl-4">
                    <div className="text-[#666666] font-medium mb-2">Rent</div>
                    <div
                      onClick={() => handleMobileNavigation("/rent/short", 0)}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <LightIcon />
                      <div className="text-[#666666]">Short Term</div>
                    </div>
                    <div
                      onClick={() => handleMobileNavigation("/rent/all", 0)}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <LightIcon />
                      <div className="text-[#666666]">All Properties</div>
                    </div>
                    <div
                      onClick={() => handleMobileNavigation("/rent/long", 0)}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <LightIcon />
                      <div className="text-[#666666]">Long Term</div>
                    </div>
                  </div>

                  {/* Dashboard Submenu */}
                  <div className="border-l-2 border-[#f0f0f0] pl-4">
                    <div className="text-[#666666] font-medium mb-2">Dashboard</div>
                    <div
                      onClick={() => handleMobileNavigation("/dashboard/host/dashboard", 1)}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <HandKey />
                      <div className="text-[#666666]">Host mode</div>
                    </div>
                    <div
                      onClick={() => handleMobileNavigation("/dashboard/traveler/dashboard", 1)}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <Airplane />
                      <div className="text-[#666666]">Traveler mode</div>
                    </div>
                  </div>

                  {/* Dashboard Submenu - always shown */}
                  {/* <div className="border-l-2 border-[#f0f0f0] pl-4">
                    <div className="text-[#666666] font-medium mb-2">Dashboard</div>
                    <div
                      onClick={() => handleMobileNavigation("/dashboard/host/dashboard", isPerpsPage ? 1 : 1)}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <HandKey />
                      <div className="text-[#666666]">Host mode</div>
                    </div>
                    <div
                      onClick={() => handleMobileNavigation("/dashboard/traveler/dashboard", isPerpsPage ? 1 : 1)}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <Airplane />
                      <div className="text-[#666666]">Traveler mode</div>
                    </div>
                  </div> */}

                  {/* Mobile Connect Wallet */}
                  <div className="border-t pt-4">
                    <div
                      onClick={() => {
                        if (isConnected) setShowConnect(!showConnect);
                        else tryConnect();
                        setShowMobileMenu(false);
                      }}
                      className="w-full"
                    >
                      <PurpleButton
                        text={
                          isConnected
                            ? truncateWalletAddress(account)
                            : "Connect Wallet"
                        }
                      />
                    </div>

                    {isConnected && (
                      <div className="w-full mt-3">
                        <RedButton
                          text="Disconnect"
                          onClick={() => {
                            handleDisconnect();
                            setShowMobileMenu(false);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
};